/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

import { db, auth } from "../../../firebaseConfig";
import ConfirmModal from "../modals/confirmModal";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import "./RecentView.css";
import "./RecentLibrary.css";
// import { Typography, Tooltip } from "@mui/material";
import RecentViewSkeleton from "./RecentViewSkeleton";

//import for creating new views
// import { getFunctions, httpsCallable } from "firebase/functions";
import { createNewViewWithUniqueID, duplicateView } from "../Functions/viewHelpers";
import { useNavigate } from "react-router-dom";

function RecentViews({ isMenu, showActions, limit, onNavigate, setIsLoading: parentSetIsLoading }) {
  const [viewIDs, setViewIDs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);
  const [userUID, setUserUID] = useState(() => auth?.currentUser?.uid || null);
  const [tempTitles, setTempTitles] = useState({}); // For editable titles
  const [, setDatasetIDs] = useState([]);
  const lastUserUidRef = useRef(userUID);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState({});
  // const [displayedDatasets, setDisplayedDatasets] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (typeof onAuthStateChanged !== "function") {
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid || null;
      if (lastUserUidRef.current === nextUid) {
        return;
      }
      lastUserUidRef.current = nextUid;
      setUserUID(nextUid);
    });
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (!userUID) {
      setViewIDs([]);
      setDatasetIDs([]);
      setIsLoading(false);
      if (parentSetIsLoading) parentSetIsLoading(false);
      return;
    }

    const fetchCollection = async (collectionName) => {
        const createdByQuery = query(collection(db, collectionName), where("createdBy", "==", userUID));
        const collaboratorQuery = query(
          collection(db, collectionName),
          where("collaborators", "array-contains", doc(db, "users", userUID))
        );
  
        try {
          const [createdBySnapshot, collaboratorSnapshot] = await Promise.all([
            getDocs(createdByQuery),
            getDocs(collaboratorQuery),
          ]);
  
          const allDocs = new Map();
          createdBySnapshot.docs.forEach((doc) => allDocs.set(doc.id, doc));
          collaboratorSnapshot.docs.forEach((doc) => allDocs.set(doc.id, doc));
  
          const snapshot = { docs: Array.from(allDocs.values()) };
  
          const toMillis = (ts) => {
            if (!ts) return 0;
            if (typeof ts === "number") return ts;
            if (typeof ts.toMillis === "function") return ts.toMillis();
            if (typeof ts.seconds === "number") return ts.seconds * 1000;
            if (typeof ts._seconds === "number") return ts._seconds * 1000; // compatibility
            return 0;
          };
  
          const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data()}));
  
          items.sort((a, b) => {
            const timeA = toMillis(a.editedAt) || toMillis(a.createdAt);
            const timeB = toMillis(b.editedAt) || toMillis(b.createdAt);
            return timeB - timeA;
          });

          return items;
        } catch (error) {
          console.error(`Error fetching ${collectionName}:`, error);
          return [];
        }
      };

    const fetchBothCollections = async () => {
      setIsLoading(true);
      if (parentSetIsLoading) parentSetIsLoading(true);
      
      const [fetchedViews, fetchedDatasets] = await Promise.all([
        fetchCollection("views"),
        fetchCollection("datasets"),
      ]);

      setViewIDs(fetchedViews);
      setDatasetIDs(fetchedDatasets);

      const userIds = new Set();
      [...fetchedViews, ...fetchedDatasets].forEach(item => {
        if (item.createdBy) userIds.add(item.createdBy);
        if (item.editedBy) userIds.add(item.editedBy);
      });

      if (userIds.size > 0) {
        const usersData = {};
        const uidsArray = Array.from(userIds);
        const promises = [];
        for (let i = 0; i < uidsArray.length; i += 30) {
            const batch = uidsArray.slice(i, i + 30);
            if (batch.length > 0) {
                const q = query(collection(db, "users"), where("__name__", "in", batch));
                promises.push(getDocs(q));
            }
        }

        const snapshots = await Promise.all(promises);
        snapshots.forEach(snapshot => {
            snapshot.forEach(doc => {
                usersData[doc.id] = doc.data();
            });
        });
        setUsers(usersData);
      }

      setIsLoading(false);
      if (parentSetIsLoading) parentSetIsLoading(false);
    };

    fetchBothCollections();
  }, [userUID, parentSetIsLoading]);

  const handleDelete = async () => {
    if (viewToDelete) {
      try {
        await deleteDoc(doc(db, "views", viewToDelete));
        setViewIDs((prev) => prev.filter((view) => view.id !== viewToDelete));
        console.log("Project deleted successfully!");
      } catch (error) {
        console.error("Error deleting project:", error);
      }
      setIsModalOpen(false);
    }
  };

  const displayedViews = limit ? viewIDs.slice(0, limit) : viewIDs;
  // const displayedDatasets = limit ? datasetIDs.slice(0, limit) : datasetIDs; // currently unused

  const handleTemporaryTitleChange = (newTempValue, libraryId) => {
    setTempTitles((prevTempTitles) => ({
      ...prevTempTitles,
      [libraryId]: newTempValue,
    }));
  };
  const updateTitleInFirestore = async (newTitle, currentID) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      return;
    }
    const docRef = doc(db, "views", currentID); // Corrected: Target "views" collection

    try {
      await updateDoc(docRef, { title: newTitle });
      //console.log("Title updated in Firestore.");
    } catch (error) {
      console.error("Error updating title in Firestore:", error);
    }
  };

  const handleInputKeyDown = async (event, libraryId) => {
    const viewItem = viewIDs.find((v) => v.id === libraryId); // Corrected: use viewItem
    if (!viewItem) return;

    const originalTitle = viewItem.title; // Corrected: use viewItem.title
    const newTitle = tempTitles[libraryId];

    if (event.key === "Enter") {
      // Handles Enter, Meta+Enter, Cmd+Enter, Ctrl+Enter
      event.preventDefault();

      const trimmedNewTitle = newTitle !== undefined ? newTitle.trim() : "";

      // Save if the new title is valid, has changed, and is not empty
      if (newTitle !== undefined && trimmedNewTitle !== "" && trimmedNewTitle !== originalTitle) {
        setViewIDs((prevViewIDs) => prevViewIDs.map((lib) => (lib.id === libraryId ? { ...lib, title: trimmedNewTitle } : lib)));
        await updateTitleInFirestore(trimmedNewTitle, libraryId);
      }

      // Clear temporary title for this item and blur input, regardless of save
      setTempTitles((prevTempTitles) => {
        const updatedTempTitles = { ...prevTempTitles };
        delete updatedTempTitles[libraryId];
        return updatedTempTitles;
      });
      event.target.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      // Revert to original title by clearing the temporary title
      setTempTitles((prevTempTitles) => {
        const updatedTempTitles = { ...prevTempTitles };
        delete updatedTempTitles[libraryId];
        return updatedTempTitles;
      });
      event.target.blur();
    }
  };

  if (isLoading) {
    return <RecentViewSkeleton count={limit || 5} />;
  }

  // Normalize Firestore Timestamp or number epoch millis to JS millis
  const toMillis = (ts) => {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    if (typeof ts._seconds === "number") return ts._seconds * 1000;
    return null;
  };

  const getUserDisplayName = (uid) => {
    if (uid === userUID) return "you";
    const user = users[uid];
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  }

  return (
    <>
      {displayedViews.length == 0 ? (
        <div className="cards">
          <div className="nocontent">
            <Link
              onClick={() => {
                if (onNavigate) {
                  onNavigate();
                }
                createNewViewWithUniqueID(
                  "blank-template",
                  "blankTemplate",
                  //setCurrentViewUID,
                  navigate
                );
              }}
            >
              <img src="/assets/images/feature-slide-collab-app.png"></img>
            </Link>
          </div>
        </div>
      ) : (
        <>
          <ul className={isMenu ? "nav-menu-list" : "library-list"} >
            {/* {displayedDatasets.map((view) => {
      const displayTitle = tempTitles[view.id] !== undefined ? tempTitles[view.id] : view.name;
      return (
        <li className="recentsList" key={view.id} style={{ display: "space-between", alignItems: "center", marginBottom: "0.5em" }}>
          <Link
            to={`/statistics/?u=${userUID}&d=${view.id}`}
            onClick={(e) => {
              if (e.target.tagName === "INPUT" || tempTitles[view.id] !== undefined) {
                e.preventDefault();
              }
            }}
          >
            <div className="recentsListItem">
              <div className="viewTitleInput">
                <input
                  type="text"
                  placeholder={view.title || "My Project"}
                  value={displayTitle || ""}
                  onChange={(e) => handleTemporaryTitleChange(e.target.value, view.id)}
                  onKeyDown={(e) => handleInputKeyDown(e, view.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              </div>
            </div>
          </Link>

          <div>
            {showActions && (
              <button
                onClick={() => duplicateView(view.id, "views", navigate)}
                className="delBtn"
              >
                <ContentCopyIcon />
              </button>
            )}

            {showActions && (
              <button
                onClick={() => {
                  setViewToDelete(view.id);
                  setIsModalOpen(true);
                }}
                className="delBtn"
              >
                <DeleteIcon />
              </button>
            )}
          </div>
        </li>
      );
    })} */}

            {displayedViews.map((view) => {
              const displayTitle = tempTitles[view.id] !== undefined ? tempTitles[view.id] : view.title;
              return (
                <li className={isMenu ? "nav-menu-item" : "view-item"} key={view.id}>
                  <Link
                    to={`/views/${view.id}`}
                    style={{ display: "flex", width: "100%", height: isMenu ? "44px" : "", marginRight: isMenu ? "0px" : "", padding: isMenu ? "0px" : ""}}
                    onClick={(e) => {
                      // Prevent navigation if the click is on the input itself or if a temp title exists for editing
                      if (e.target.tagName === "INPUT" || tempTitles[view.id] !== undefined) {
                        e.preventDefault();
                      } else {
                        if (onNavigate) {
                          onNavigate();
                        }
                      }
                    }}
                  >
                    {!isMenu ? (
                    <div style={{ backgroundColor: "var(--Grey2)" }}>
                      <svg className="library-item-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                        <path
                          fill="var(--Grey7)"
                          d="M21 2H3a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1M8 20H4V4h4Zm12 0H10v-7h10Zm0-9H10V4h10Z"
                        />
                      </svg>
                    </div>
                    ) : null} 
                    
                    <div className="library-item-content" style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, gap: "0.5em" }}>
                      <div
                        className="library-item-title"
                        style={{ display: "flex", alignItems: "center", width: "100%" , justifyContent: "flex-start" }}
                      >
                        <div className="recentTitleInput" style={{ width: "100%" }}>
                          {isMenu ? (
                            <div style={{
                              padding: "8px 0 8px 10px",
                              margin: "0 0 0 6px",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color: "var(--Grey6)",
                              backgroundColor: "transparent",
                              border: "1px solid transparent",
                              width: "100%",
                              boxSizing: "border-box",
                            }}>
                              {displayTitle || "View Name"}
                            </div>
                          ) : !showActions ? (
                            <div
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                padding: "1px 2px", // Approximating input padding
                                border: "1px solid transparent", // Mimicking input border
                                backgroundColor: "transparent",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                paddingLeft: "20px",
                              }}
                            >
                              {displayTitle || "View Name"}
                            </div>
                          ) : (
                          <input
                            type="text"
                            name="viewItem"
                            placeholder="View Name"
                            value={displayTitle || ""}
                            onChange={(e) => handleTemporaryTitleChange(e.target.value, view.id)}
                            onKeyDown={(e) => handleInputKeyDown(e, view.id)}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent Link's onClick from firing
                              e.preventDefault(); // Prevent default click action on input (usually focusing, might help ensure focus is kept)
                            }}
                            style={{ width: "100%", boxSizing: "border-box"}}
                          />
                          )}
                        </div>
                      </div>
                      
                      {!isMenu? (
                      <div className="library-item-metadata">
                        {toMillis(view.createdAt) && <p><span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>CREATED </span><br/>{new Date(toMillis(view.createdAt)).toLocaleDateString()}</p>}
                        <p><span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>OWNER </span> <br/>{getUserDisplayName(view.createdBy)}</p>
                        {toMillis(view.editedAt) &&  <p><span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>LAST EDIT</span><br/>{new Date(toMillis(view.editedAt)).toLocaleDateString()}</p>}
                        {view.editedBy && (
                            <p><span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>LAST EDITED </span><br/>{getUserDisplayName(view.editedBy)}</p>
                        )}
                      </div>
                      ) : null}
                      
                    </div>
                  </Link>
                  {showActions && (

                  <div className="library-item-actions">
                      <button
                        onClick={() => {
                          duplicateView(view.id, "views", navigate);
                        }}
                        className="duplicateBtn"
                      >
                        <ContentCopyIcon />
                      </button>
                      <button
                        onClick={() => {
                          setViewToDelete(view.id);
                          setIsModalOpen(true);
                        }}
                        className="delBtn"
                      >
                        <DeleteIcon />
                      </button>
                  </div>
                   )}

                </li>
              );
            })}
          </ul>
          <ConfirmModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} />
        </>
      )}
    </>
  );
}

export default RecentViews;